import React, { useEffect, useState } from "react";
import { AdminShell } from "../components/AdminShell";
import { api } from "../lib/api";
import { invalidatePublicSettings } from "../lib/publicSettings";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Landmark, Phone, Bell, MessageCircle } from "lucide-react";

export default function AdminSettings() {
  const [form, setForm] = useState({
    iban: "",
    bank_name: "",
    account_holder: "",
    bic_swift: "",
    deposit_amount: 500,
    payment_instructions: "",
    contact_phone: "",
    contact_email: "",
    contact_address: "",
    opening_hours: "",
    whatsapp_number: "",
    notification_email: "",
  });
  const [loading, setLoading] = useState(false);
  const [initial, setInitial] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/settings");
        setForm({
          iban: r.data.iban || "",
          bank_name: r.data.bank_name || "",
          account_holder: r.data.account_holder || "",
          bic_swift: r.data.bic_swift || "",
          deposit_amount: r.data.deposit_amount ?? 500,
          payment_instructions: r.data.payment_instructions || "",
          contact_phone: r.data.contact_phone || "",
          contact_email: r.data.contact_email || "",
          contact_address: r.data.contact_address || "",
          opening_hours: r.data.opening_hours || "",
          whatsapp_number: r.data.whatsapp_number || "",
          notification_email: r.data.notification_email || "",
        });
      } catch { toast.error("Errore caricamento impostazioni"); }
      finally { setInitial(false); }
    })();
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/settings", {
        ...form,
        deposit_amount: parseFloat(form.deposit_amount) || 500,
      });
      invalidatePublicSettings();
      toast.success("Impostazioni salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setLoading(false); }
  };

  return (
    <AdminShell title="Impostazioni">
      {initial ? (
        <div className="text-white/50 p-10 text-center">Caricamento...</div>
      ) : (
        <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="settings-form">
          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Phone size={20} className="text-gold" />
              <h3 className="font-display text-xl font-semibold text-gold">Contatti pubblici</h3>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-6">
              Questi dati appaiono nel footer di ogni pagina, nella pagina "Contatti", e nei pulsanti "Chiama Ora" / "Invia Email" della scheda auto.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Telefono</Label>
                <Input
                  value={form.contact_phone}
                  onChange={update("contact_phone")}
                  placeholder="Es: +39 348 123 4567"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-contact-phone"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Email</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={update("contact_email")}
                  placeholder="Es: info@guzzardella-auto.it"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-contact-email"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Indirizzo</Label>
                <Input
                  value={form.contact_address}
                  onChange={update("contact_address")}
                  placeholder="Es: Via Roma 12, 20100 Milano (MI)"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-contact-address"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Orari apertura</Label>
                <Textarea
                  value={form.opening_hours}
                  onChange={update("opening_hours")}
                  rows={4}
                  placeholder={"Lun - Ven: 9:00 - 19:00\nSabato: 9:00 - 13:00\nDomenica: Chiuso"}
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white font-mono text-sm"
                  data-testid="settings-opening-hours"
                />
                <p className="text-xs text-white/40 mt-2">Una riga per giorno. Verrà mostrato nel footer.</p>
              </div>
            </div>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle size={20} style={{ color: "#25D366" }} />
              <h3 className="font-display text-xl font-semibold" style={{ color: "#25D366" }}>WhatsApp</h3>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-6">
              Se compilato, ogni scheda auto mostrerà un pulsante "Contatta su WhatsApp" con messaggio precompilato. Inserisci il numero con prefisso internazionale (senza spazi ne trattini).
            </p>
            <Label className="text-xs uppercase tracking-widest text-white/60">Numero WhatsApp</Label>
            <Input
              value={form.whatsapp_number}
              onChange={update("whatsapp_number")}
              placeholder="Es: +393481234567"
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white font-mono"
              data-testid="settings-whatsapp"
            />
            <p className="text-xs text-white/40 mt-2">Lascia vuoto per nascondere il pulsante WhatsApp.</p>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Bell size={20} className="text-gold" />
              <h3 className="font-display text-xl font-semibold text-gold">Notifiche Email</h3>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-6">
              Indirizzo email dove ricevere gli avvisi automatici quando arriva una nuova prenotazione, proposta di acquisto, richiesta assicurazione o demolizione. Se lasciato vuoto, verrà usato l'indirizzo dei contatti pubblici.
            </p>
            <Label className="text-xs uppercase tracking-widest text-white/60">Email per notifiche</Label>
            <Input
              type="email"
              value={form.notification_email}
              onChange={update("notification_email")}
              placeholder="Es: rocco@guzzardella-auto.it"
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="settings-notification-email"
            />
            <p className="text-xs text-white/40 mt-2">Le notifiche vengono inviate solo se l'integrazione email è attiva sul server.</p>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Landmark size={20} className="text-gold" />
              <h3 className="font-display text-xl font-semibold text-gold">Coordinate bancarie</h3>
            </div>
            <p className="text-xs text-white/50 leading-relaxed mb-6">
              Questi dati saranno mostrati ai clienti quando cliccano "Blocca questa auto" e devono effettuare il bonifico della caparra.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">Intestatario del conto</Label>
                <Input
                  value={form.account_holder}
                  onChange={update("account_holder")}
                  placeholder="Es: Rocco Andrea Guzzardella"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-holder"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Banca</Label>
                <Input
                  value={form.bank_name}
                  onChange={update("bank_name")}
                  placeholder="Es: Intesa Sanpaolo"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-bank"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">BIC / SWIFT</Label>
                <Input
                  value={form.bic_swift}
                  onChange={update("bic_swift")}
                  placeholder="Es: BCITITMM"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-bic"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-white/60">IBAN</Label>
                <Input
                  value={form.iban}
                  onChange={update("iban")}
                  placeholder="IT00 X000 0000 0000 0000 0000 000"
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white font-mono"
                  data-testid="settings-iban"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Importo caparra (€)</Label>
                <Input
                  type="number"
                  step="10"
                  value={form.deposit_amount}
                  onChange={update("deposit_amount")}
                  className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
                  data-testid="settings-deposit"
                />
              </div>
            </div>
          </div>

          <div className="bg-midnight-light border border-white/5 p-6">
            <Label className="text-xs uppercase tracking-widest text-white/60">Istruzioni bonifico (opzionale)</Label>
            <Textarea
              value={form.payment_instructions}
              onChange={update("payment_instructions")}
              rows={4}
              placeholder="Es: Effettuare il bonifico entro 48 ore dalla prenotazione. La causale deve contenere il tuo cognome..."
              className="mt-2 bg-white/5 border-white/10 rounded-sm text-white"
              data-testid="settings-instructions"
            />
            <p className="text-xs text-white/40 mt-2">Mostrato al cliente nel dialogo di prenotazione.</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-gold px-8 py-3 text-xs uppercase tracking-widest font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            <Save size={14} /> {loading ? "Salvataggio..." : "Salva Impostazioni"}
          </button>
        </form>
      )}
    </AdminShell>
  );
}
